import React from "react"
import PropTypes from "prop-types"
import { Route, Switch, withRouter } from "react-router-dom"
import Helpers from "utils/Helpers"
import { connect } from "react-redux"
import Done from "components/views/reimbursements/Done"
import CarrotClient from "lib/carrot-api/index"
import Submit from "components/views/reimbursements/Submit"
import Upload from "components/views/reimbursements/upload/Upload"
import AttachmentStates from "utils/AttachmentStates"
import { v4 as uuid } from "uuid"
import { getReimbursementsList } from "reducers/reimbursements"
import Settings from "../../../utils/CarrotConfig"
import { fetchUserInfo } from "actions/userActions"
import { getIsDragging } from "reducers/application"
import { resetDragState } from "actions/applicationActions"
import { PageNotFound } from "components/views/error/PageNotFound"
import { MasterLayout } from "../../views/atoms/layouts/MasterLayout"
import { BackToPageHeader } from "../../views/molecules/Molecules"
import { IvfRequirementsContainer } from "components/containers/reimbursements/IvfRequirementsContainer"
import { CarrotRoute, getReimbursementUrl } from "utils/CarrotUrls"
import queryString from "querystring"

const requiredProperties = ["whoReceivedCare", "isInfertility", "isMedicalNecessity"]

const client = new CarrotClient(Settings.CARROT_BACKEND_URL)

const { UPLOADING, COMPLETED, FAILED, OVERSIZED } = AttachmentStates

const MAX_FILE_SIZE = 30 * 1000000 // 30 MB

const emptyState = {
  fields: {
    whoReceivedCare: {
      value: [],
      valid: true
    },
    isInfertility: {
      value: "",
      valid: true
    },
    isMedicalNecessity: {
      value: "",
      valid: true
    },
    memberNotes: {
      value: "",
      valid: true
    }
  },
  submitting: false,
  showErrorMessage: false,
  formNotComplete: false,
  attachments: []
}

class SubmitReimbursementContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = JSON.parse(JSON.stringify(emptyState))
  }

  formNotComplete() {
    const { fields } = this.state
    requiredProperties.forEach((property) => {
      fields[property].valid = fields[property].value.length > 0
    })

    // check if any fields are empty
    const formNotComplete = !Object.values(fields)
      .map((field) => field.valid)
      .every((a) => a === true)

    this.setState({ fields, formNotComplete })
    return formNotComplete
  }

  async addReimbursement(
    fileGuids,
    whoReceivedCare,
    isInfertility,
    isMedicalNecessity,
    memberNotes,
    expenseId,
    expenseJourneyType
  ) {
    const isCardExpense = !!expenseId

    if (isCardExpense) {
      await client.submitCardExpense(
        fileGuids,
        whoReceivedCare,
        isInfertility,
        isMedicalNecessity,
        memberNotes,
        expenseId,
        expenseJourneyType
      )
    } else {
      await client.submitExpense(
        fileGuids,
        whoReceivedCare,
        isInfertility,
        isMedicalNecessity,
        memberNotes,
        expenseJourneyType
      )
    }
  }

  onSubmitForm = async (expenseId) => {
    if (this.formNotComplete()) {
      return
    }
    const { fields } = this.state
    const reimbursementTypeQueryString = queryString.parse(this.props.location.search)["?reimbursement-type"]

    this.setState({ submitting: true, showErrorMessage: false })

    try {
      await this.addReimbursement(
        this.state.attachments.map((attachment) => attachment.guid),
        fields.whoReceivedCare.value,
        fields.isInfertility.value,
        fields.isMedicalNecessity.value,
        fields.memberNotes.value,
        expenseId,
        reimbursementTypeQueryString
      )
    } catch (error) {
      this.setState({ showErrorMessage: true, submitting: false })
      return
    }

    this.props.fetchUserInfo()
    this.setState(emptyState)

    const isCardExpense = () => !!expenseId
    this.props.history.push(`/reimbursements/${isCardExpense() ? "carrot-card-submit-success" : "submit-success"}`)
  }

  onDropRejected = () => {
    this.props.resetDragState()
  }

  onDropAccepted = (files) => {
    this.props.resetDragState()

    const newAttachments = files.map((file) => ({
      file: file,
      state: file.size > MAX_FILE_SIZE ? OVERSIZED : UPLOADING,
      guid: uuid()
    }))

    this.setState({
      attachments: [...this.state.attachments, ...newAttachments]
    })

    const attachmentsToUpload = newAttachments.filter((a) => a.file.size <= MAX_FILE_SIZE)

    attachmentsToUpload.forEach(async (attachment) => {
      await this.uploadFile(attachment)
    })
  }

  uploadFile = async (attachment) => {
    const guid = attachment.guid
    try {
      await client.uploadReimbursementFile(attachment.file, guid)
      this.onFileUploadSuccess(guid)
    } catch {
      this.onFileUploadFailure(guid)
    }
  }

  onFileUploadSuccess = (guid) => {
    this.setAttachmentState(guid, COMPLETED)
  }

  onFileUploadFailure = (guid) => {
    this.setAttachmentState(guid, FAILED)
  }

  setAttachmentState = (guid, state) => {
    const attachments = [...this.state.attachments]
    const attachment = this.findAttachmentByGuid(attachments, guid)
    if (!attachment) return // a member cancelled mid-upload

    attachment.state = state
    this.setState({ attachments: attachments })
  }

  findAttachmentByGuid = (attachments, guid) => attachments.find((attachment) => attachment.guid === guid)

  onNext = (expenseId, reimbursementType) => {
    const reimbursementUrl = getReimbursementUrl(CarrotRoute.Submit, expenseId, reimbursementType)
    this.props.history.push(reimbursementUrl)
  }

  onFinish = () => {
    this.props.history.push("/transactions")
  }

  onDelete = (guid) => {
    this.setState({
      attachments: this.state.attachments.filter((attachment) => attachment.guid !== guid)
    })
  }

  onRetry = async (guid) => {
    const attachment = this.findAttachmentByGuid(this.state.attachments, guid)

    this.setAttachmentState(guid, UPLOADING)
    await this.uploadFile(attachment)
  }

  handleInputChange = (event) => {
    Helpers.handleInputChangeFieldsDeprecated.bind(this)(event)

    const { fields } = this.state
    if (requiredProperties.includes(event.target.name)) {
      fields[event.target.name].valid = fields[event.target.name].value.length > 0
      this.setState({ fields })
    }
  }

  render() {
    return (
      <Switch>
        <Route
          exact
          path="/reimbursements/upload/:expenseId?"
          render={(props) => (
            <Upload
              {...props}
              expenseId={props.match.params.expenseId || ""}
              attachments={this.state.attachments}
              draggingFile={this.props.draggingFile}
              onDropAccepted={this.onDropAccepted}
              onDropRejected={this.onDropRejected}
              onDelete={this.onDelete}
              onRetry={this.onRetry}
              onDone={this.onNext}
              onClose={this.onFinish}
              reimbursements={this.props.reimbursements}
            />
          )}
        />

        <Route
          exact
          path="/reimbursements/submit/:expenseId?"
          render={(props) => (
            <Submit
              {...props}
              expenseId={props.match.params.expenseId || ""}
              formNotComplete={this.state.formNotComplete}
              showErrorMessage={this.state.showErrorMessage}
              fields={this.state.fields}
              onDone={this.onSubmitForm}
              sending={this.state.submitting}
              attachments={this.state.attachments}
              handleInputChange={this.handleInputChange}
            />
          )}
        />

        <Route exact path="/reimbursements/ivf-requirements/:expenseId?" render={() => <IvfRequirementsContainer />} />

        <Route
          exact
          path={`${CarrotRoute.IvfRequirementsSigned}/:expenseId?`}
          render={() => <IvfRequirementsContainer signed />}
        />

        <Route
          exact
          path="/reimbursements/carrot-card-submit-success"
          render={() => (
            <MasterLayout header={<DoneHeader />}>
              <Done carrotCard onDone={this.onFinish} />
            </MasterLayout>
          )}
        />

        <Route
          exact
          path="/reimbursements/submit-success"
          render={() => (
            <MasterLayout header={<DoneHeader />}>
              <Done carrotCard={false} onDone={this.onFinish} />
            </MasterLayout>
          )}
        />

        <Route component={PageNotFound} />
      </Switch>
    )
  }
}

const DoneHeader = () => <BackToPageHeader path="/transactions" pathName="Transactions" />

SubmitReimbursementContainer.propTypes = {
  fetchUserInfo: PropTypes.func.isRequired,
  draggingFile: PropTypes.bool.isRequired,
  history: PropTypes.object.isRequired
}

const mapStateToProps = (state) => ({
  reimbursements: getReimbursementsList(state),
  draggingFile: getIsDragging(state)
})

SubmitReimbursementContainer = connect(mapStateToProps, {
  fetchUserInfo,
  resetDragState
})(SubmitReimbursementContainer)

export default withRouter(SubmitReimbursementContainer)
